import { useState } from 'react';
import { Actions, FSpread } from '@client/new/helpers/types';
import { Control, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ButtonVariant } from '@client/new/pages/workout/components/addFromLibrary/mappers';
import toast from 'react-hot-toast';
import {
  PriceRangeType,
  transformPrice,
} from '../bundles/subscription/viewModel/editBundle.vm.mapper';
import {
  createBundlePriceFormSchema,
  CreateFormValues,
  formDefaultValues,
} from '../bundles/subscription/viewModel/priceFormSchemas';
import { mapToCreateBundleInput } from '../bundles/subscription/viewModel/createBundle.mapper';
import { useCreateBundleModel } from '../bundles/subscription/model/createBundle.model';
import { browserHistory } from '@client/router/compat';
import { PATHS } from '@client/routes';
import {
  HelpEventType,
  toHelpMixpanelEvent,
} from '../bundles/subscription/viewModel/subscriptionBundle.vm.mapper';

type PayloadMap = {
  start: null;
  setStep: SetupModalSteps;
  create: null;
  close: null;
  cancel: null;
  transformPrice: {
    value: string;
    type: PriceRangeType;
  };
  helpMaterialsEvent: HelpEventType;
};

type SetupFirstBundleAction = Actions<PayloadMap>;
export type SetupFirstBundleDispatcher = FSpread<PayloadMap>;

export type SetupFirstBundleVM = {
  setupBundleModal: {
    dispatch: SetupFirstBundleDispatcher;
    isOpen: boolean;
    close: () => void;
    control: Control<CreateFormValues, any, CreateFormValues>;
    error?: string;
    transformPrice: (value: string, type: PriceRangeType) => void;
    step: SetupModalSteps;
    stepCta: Array<{
      title: string;
      onClick: () => void;
      submitting?: boolean;
      variant?: ButtonVariant;
    }>;
    price: {
      disabled: {
        monthly: boolean;
        annual: boolean;
      };
    };
  };
  setupBundle: () => void;
};

export type SetupModalSteps = 1;

export const useSetupFirstBundleVM = ({
  creatorId,
}: {
  creatorId: number;
}): SetupFirstBundleVM => {
  const [isSubmitting, setSubmitting] = useState(false);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [step, setStep] = useState<SetupModalSteps>(1);
  const { createBundle } = useCreateBundleModel();

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<CreateFormValues>({
    defaultValues: formDefaultValues,
    resolver: zodResolver(createBundlePriceFormSchema),
  });

  const annualPriceEnabled = watch('annualPriceEnabled');
  const monthlyPriceEnabled = watch('monthlyPriceEnabled');

  const onCreate = handleSubmit(
    (values) => {
      const input = mapToCreateBundleInput(values, creatorId);
      createBundle(input)
        .then(() => {
          setSubmitting(false);
          setModalOpen(false);
          browserHistory.push(PATHS.NEW_PROGRAM(creatorId));
        })
        .catch((e) => {
          toast.error(e.message);
          setSubmitting(false);
        });
    },
    () => {
      setSubmitting(false);
    },
  );

  const dispatch: SetupFirstBundleDispatcher = (type, ...args) => {
    const action = { type, payload: args[0] } as SetupFirstBundleAction;
    switch (action.type) {
      case 'create':
        setSubmitting(true);
        onCreate();
        return null;
      case 'transformPrice':
        transformPrice(action.payload.value, action.payload.type, setValue);
        return null;
      case 'close':
      case 'cancel':
        reset(formDefaultValues, {
          keepValues: false,
          keepDirty: false,
          keepDirtyValues: false,
        });
        setModalOpen(false);
        setStep(1);
        return null;
      case 'start':
        setModalOpen(true);
        return null;
      case 'setStep':
        setStep(action.payload);
        return null;
      case 'helpMaterialsEvent':
        toHelpMixpanelEvent(action.payload);
        return null;
    }
  };

  return {
    setupBundle: () => dispatch('start'),
    setupBundleModal: {
      dispatch,
      isOpen: modalOpen,
      close: () => dispatch('cancel'),
      control: control,
      error: errors.monthlyPriceEnabled?.message,
      transformPrice: (value: string, type: PriceRangeType) =>
        dispatch('transformPrice', { value, type }),
      step,
      stepCta: [
        {
          title: 'Save & Proceed',
          onClick: () => dispatch('create'),
          submitting: isSubmitting,
        },
      ],
      price: {
        disabled: {
          monthly: !monthlyPriceEnabled,
          annual: !annualPriceEnabled,
        },
      },
    },
  };
};
